import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSpinradContext } from '../../context/SpinradContext';
import { Card } from '../../types';
import { dayLabel } from '../../utils';
import OverviewCard from '../overviewCard';
import {
  CardStack,
  PlaceholderSlot,
  SampleMagnet,
  SampleNote,
  SnHint,
  SnQ,
  SnTag,
  StackBadge,
  TimelineTrack,
  TlArrow,
  TlWrap,
  WeekChip,
  WeekCol,
  WeekKw,
  WeekNode,
  WeekRange,
} from './styles';

const offsetX = 10;
const offsetY = 11;
const CARD_MAX_H = 176;
const MAX_VISIBLE_STACK = 3;

interface DayGroup {
  key: string;
  big: string;
  small: string;
  sortKey: number;
  items: Card[];
}

const groupByDay = (cards: Card[]): DayGroup[] => {
  const groups: Record<string, DayGroup> = {};
  cards.forEach(c => {
    const w = dayLabel(c.createdAt);
    if (!groups[w.key]) groups[w.key] = { ...w, items: [] };
    groups[w.key].items.push(c);
  });
  return Object.values(groups).sort((a, b) => a.sortKey - b.sortKey);
};

const Stack: React.FC<{ group: DayGroup }> = ({ group }) => {
  const { openModal } = useSpinradContext();
  const display = [...group.items].sort(
    (a, b) => b.createdAt.getTime() - a.createdAt.getTime(),
  );
  const deckIds = display.map(c => c.id).reverse();
  const n = display.length;
  const newestId = deckIds[deckIds.length - 1];
  const visible = display.slice(0, MAX_VISIBLE_STACK);
  const visibleCount = visible.length;
  const visibleKey = visible.map(c => c.id).join(',');

  const cardElsRef = useRef<Map<number, HTMLDivElement>>(new Map());
  const [stackHeight, setStackHeight] = useState(CARD_MAX_H);

  // Every visible card (not just the front one) is measured, because each
  // is absolutely positioned at its own top offset — a shorter front card
  // doesn't stop a taller card further back in the deck from overflowing.
  // useLayoutEffect (not useEffect) applies the measurement before the
  // browser paints, so a card taller than the CARD_MAX_H default doesn't
  // get its bottom clipped by the timeline's overflow for one visible frame.
  useLayoutEffect(() => {
    const entries = visible
      .map((c, i) => ({ i, el: cardElsRef.current.get(c.id) }))
      .filter((e): e is { i: number; el: HTMLDivElement } => !!e.el);
    if (entries.length === 0) return undefined;

    const update = (): void => {
      const required = entries.reduce(
        (max, { i, el }) => Math.max(max, i * offsetY + el.offsetHeight),
        0,
      );
      setStackHeight(prev => Math.max(prev, required));
    };
    update();

    const observer = new ResizeObserver(update);
    entries.forEach(({ el }) => observer.observe(el));
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleKey]);

  return (
    <CardStack
      $width={170 + (visibleCount - 1) * offsetX}
      $height={stackHeight + 8}
    >
      {visible.map((c, i) => (
        <OverviewCard
          key={c.id}
          ref={el => {
            if (el) cardElsRef.current.set(c.id, el);
            else cardElsRef.current.delete(c.id);
          }}
          card={c}
          variant={i === 0 ? 'front' : 'behind'}
          onClick={() => openModal(newestId, deckIds)}
          style={{
            left: i * offsetX,
            top: i * offsetY,
            zIndex: 100 - i,
            transform: `rotate(${(c.rot || 0) + (i === 0 ? 0 : i * 2.1)}deg)`,
          }}
        />
      ))}
      {n > 1 && (
        <StackBadge title={`${n} Karten an diesem Tag`}>{n}</StackBadge>
      )}
    </CardStack>
  );
};

const Timeline: React.FC = () => {
  const { cards } = useSpinradContext();
  const trackRef = useRef<HTMLDivElement>(null);
  const prevLenRef = useRef(cards.length);
  const [arrows, setArrows] = useState({ prev: false, next: false });

  const updateArrows = (): void => {
    const tl = trackRef.current;
    if (!tl) return;
    const overflow = tl.scrollWidth - tl.clientWidth;
    const canScroll = overflow > 4;
    const atStart = tl.scrollLeft <= 2;
    const atEnd = tl.scrollLeft >= overflow - 2;
    setArrows({ prev: canScroll && !atStart, next: canScroll && !atEnd });
  };

  useEffect(() => {
    const tl = trackRef.current;
    if (tl) tl.scrollLeft = tl.scrollWidth;
    updateArrows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (cards.length > prevLenRef.current) {
      const tl = trackRef.current;
      if (tl) tl.scrollLeft = tl.scrollWidth;
    }
    prevLenRef.current = cards.length;
    updateArrows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cards]);

  useEffect(() => {
    window.addEventListener('resize', updateArrows);
    return () => window.removeEventListener('resize', updateArrows);
  }, []);

  const groups = groupByDay(cards);

  return (
    <TlWrap>
      <TlArrow
        $side="left"
        $avail={arrows.prev}
        title="Ältere Karten"
        onClick={() =>
          trackRef.current?.scrollBy({ left: -260, behavior: 'smooth' })
        }
      >
        ‹
      </TlArrow>
      <TimelineTrack ref={trackRef} onScroll={updateArrows}>
        {groups.length === 0 ? (
          <WeekCol>
            <WeekNode />
            <WeekChip>
              <WeekKw>{dayLabel(new Date()).big}</WeekKw>
              <WeekRange>{dayLabel(new Date()).small}</WeekRange>
            </WeekChip>
            <SampleNote>
              <SampleMagnet />
              <SnTag>Muster-Karte</SnTag>
              <SnQ>
                So sieht eine fertige Karte aus: Frage, zugeordnete Kernwerte
                und eure Sternebewertung.
              </SnQ>
              <SnHint>
                Dreh das Rad, um eure erste echte Karte zu erstellen.
              </SnHint>
            </SampleNote>
          </WeekCol>
        ) : (
          groups.map(grp => (
            <WeekCol key={grp.key}>
              <WeekNode />
              <WeekChip>
                <WeekKw>{grp.big}</WeekKw>
                <WeekRange>{grp.small}</WeekRange>
              </WeekChip>
              <Stack group={grp} />
            </WeekCol>
          ))
        )}
        <WeekCol>
          <WeekNode $hidden />
          <WeekChip $hidden />
          <PlaceholderSlot />
        </WeekCol>
      </TimelineTrack>
      <TlArrow
        $side="right"
        $avail={arrows.next}
        title="Neuere Karten"
        onClick={() =>
          trackRef.current?.scrollBy({ left: 260, behavior: 'smooth' })
        }
      >
        ›
      </TlArrow>
    </TlWrap>
  );
};

export default Timeline;
